import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { isModuleVisible } from '../data/displayPrefs';
import { useFeatureFlag } from '../context/FeatureFlagsContext';

// One-word, on-brand labels. `module` keys map to displayPrefs.modules so the
// nav auto-hides items the user has disabled (or never activated at onboarding).
const NAV = [
  { to: '/progress',  label: 'Progress',   module: null }, // always shown
  { to: '/strength',  label: 'Strength',   module: null }, // always shown
  { to: '/endurance', label: 'Endurance',  module: 'recovery' },
  { to: '/training',  label: 'Combatives', module: 'training' },
  { to: '/nutrition', label: 'Nutrition',  module: 'nutrition' },
  { to: '/coach',     label: 'Coach',      module: 'coach' },
  { to: '/library',   label: 'Library',    module: 'library' },
  { to: '/impact',    label: 'Impact',     module: null }, // always shown
];

const Navbar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on Escape key for keyboard a11y.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-close menu on route change — covers the edge case the user reported
  // where tapping a link that points to the CURRENT route doesn't trigger
  // navigation, leaving the menu stuck open.
  useEffect(() => { setOpen(false); }, [pathname]);

  // Marketing-front-door pattern: when an unauthenticated visitor
  // lands on Home, the navbar shows only brand + sign-in/get-started CTAs —
  // no nav links into authed pages. Reduces "look around then bounce" pattern
  // and channels everyone through the onboarding funnel. Returning authed
  // users see the full nav everywhere including Home.
  const hideNavLinks = !user && pathname === '/';
  const { enabled: coachEnabled } = useFeatureFlag('ai_coach');

  // Filter Coach link when ai_coach flag is off (staged-launch hide). Other
  // modules use the user's tracking preferences (`isModuleVisible`); Coach
  // ALSO requires the global feature flag because it's cost-bearing.
  const visibleNav = hideNavLinks ? [] : NAV.filter((n) => {
    if (n.module === 'coach' && !coachEnabled) return false;
    return !n.module || isModuleVisible(n.module, profile);
  });

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || pathname !== '/' ? 'bg-enf-surface-base backdrop-blur border-b border-enf-surface-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-brand">
          {/* Wordmark PNG was authored for dark bg. On sand (Adaptive mode) the
              imperfect alpha edges around the flaming "e" become visible. The
              .enf-brand-pill class applies a dark pill backdrop ONLY when body
              has data-mode="adaptive" (rule lives in index.css). In Athlete
              mode the wrapper is invisible (transparent, no padding). */}
          <span className="inline-flex items-center transition-colors enf-brand-pill">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-9 w-auto group-hover:opacity-90 transition-opacity"
              loading="eager"
              decoding="async"
            />
          </span>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/roadmap'); }}
            className="hidden sm:inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.2em] font-bold bg-enf-surface-special/40 border border-enf-border-special/40 text-enf-role-info hover:border-enf-role-info hover:text-enf-text-primary transition-colors"
            data-testid="beta-badge"
            title="View the public roadmap"
          >
            Beta
          </button>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {visibleNav.map((n) => (
            <Link key={n.to} to={n.to}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                pathname === n.to ? 'text-enf-text-primary bg-enf-surface-elevated' : 'text-enf-text-secondary hover:text-enf-text-primary hover:bg-enf-surface-mid'
              }`}
              data-testid={`nav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {!user && (
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-enf-text-secondary hover:text-enf-text-primary transition-colors" data-testid="nav-signin">
              Sign in
            </Link>
          )}
          {!profile.completedOnboarding && (
            <Link to="/onboarding" className="px-5 py-2 text-sm font-semibold bg-enf-accent text-enf-accent-contrast rounded-md hover:bg-enf-accent-hover transition-colors uppercase tracking-wider">
              Get started
            </Link>
          )}
        </div>

        <button className="md:hidden text-enf-text-primary" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <>
          {/* Backdrop — captures any tap outside the menu panel and closes it.
              Sits BEHIND the panel (z-30 vs panel default) so panel taps still
              register on the panel itself. Phase 4d walkthrough fix:
              users were tapping outside the menu expecting it to dismiss. */}
          <button
            type="button"
            aria-label="Close menu"
            data-testid="navbar-mobile-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30 bg-transparent cursor-default md:hidden"
          />
          <div className="md:hidden border-t border-enf-surface-border bg-enf-surface-base relative z-40">
            <div className="px-6 py-4 flex flex-col gap-1">
              {visibleNav.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md ${pathname === n.to ? 'text-enf-text-primary bg-enf-surface-elevated' : 'text-enf-text-secondary'}`}>
                  {n.label}
                </Link>
              ))}
              {!user && (
                <Link to="/login" onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm font-medium rounded-md text-enf-text-secondary">
                  Sign in
                </Link>
              )}
              {!profile.completedOnboarding && (
                <Link to="/onboarding" onClick={() => setOpen(false)}
                  className="mt-2 px-4 py-2.5 text-sm font-semibold bg-enf-accent text-enf-accent-contrast rounded-md text-center uppercase tracking-wider">
                  Get started
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;
